/**
 * @fileoverview ClickableCard — self-contained card wrapper that opens a detail Dialog on click.
 * Import and wrap any card content; the modal state is managed internally.
 *
 * Usage:
 *   <ClickableCard title="Monthly Salary" icon="payments" iconColor="error" detailContent={<YourContent />}>
 *     <SoftBox>...card body...</SoftBox>
 *   </ClickableCard>
 */

import { useState } from "react";
import PropTypes from "prop-types";

// MUI
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// ClubOS
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

function ClickableCard({ children, title, icon, iconColor, detailContent, tooltip, sx, cardSx }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Tooltip title={tooltip || "Click for details"} placement="top" arrow>
        <Card
          onClick={() => setOpen(true)}
          sx={{
            cursor: "pointer",
            transition: "transform 0.18s, box-shadow 0.18s",
            "&:hover": {
              transform: "translateY(-3px)",
              boxShadow: "0 10px 28px rgba(0,0,0,0.13)",
            },
            ...cardSx,
          }}
        >
          {children}
        </Card>
      </Tooltip>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            fontWeight: 700,
            fontSize: "1rem",
            pb: 1,
          }}
        >
          {icon && (
            <SoftBox
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="2.2rem"
              height="2.2rem"
              borderRadius="md"
              bgColor={iconColor || "info"}
              variant="gradient"
              flexShrink={0}
            >
              <Icon sx={{ color: "white", fontSize: "1.1rem" }}>{icon}</Icon>
            </SoftBox>
          )}
          <SoftTypography variant="h6" fontWeight="bold" component="span">
            {title}
          </SoftTypography>
        </DialogTitle>

        <DialogContent sx={{ pt: 2 }}>
          {detailContent}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <SoftButton variant="outlined" color="secondary" size="small" onClick={() => setOpen(false)}>
            Close
          </SoftButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

ClickableCard.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  detailContent: PropTypes.node.isRequired,
  tooltip: PropTypes.string,
  sx: PropTypes.object,
  cardSx: PropTypes.object,
};

export default ClickableCard;
